import type {
  GetDriversWithAddressByDateQuery,
  GetDriversWithLastTrackPointQuery,
} from '@/generated/graphql/graphql';
import dayjs from '@/utils/dayjs-instance';

type Order = GetDriversWithAddressByDateQuery['user'][0]['orders'][0];
type DriverOrder =
  GetDriversWithLastTrackPointQuery['user'][0]['orders'] extends Array<infer T> ? T : never;

const timezoneToMinutes = (timeZone?: number | null): number => (timeZone ?? 0) * 60;

export const isExpressOrder = (order: Order | DriverOrder): boolean => {
  return 'delivery_provider' in order && order.delivery_provider?.short_name === 'DTK_EXPRESS';
};

const isDelivered = (order: Order | DriverOrder): boolean => {
  return (order as { statusName?: string | null }).statusName === 'Delivered';
};

export const getExpressOrderDeadline = (
  order: Order | DriverOrder,
  _departmentTimeZone?: number | null
): dayjs.Dayjs | null => {
  if (!order.plannedDate || !order.plannedDateDuration) {
    return null;
  }

  return dayjs.utc(order.plannedDate).add(order.plannedDateDuration, 'minutes');
};

export const formatIsoTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  const match = isoString.match(/T(\d{2}):(\d{2})/);
  if (!match) return '';
  return `${match[1]}:${match[2]}`;
};

export const calculateEndTime = (
  isoString: string | null | undefined,
  durationMinutes?: number | null
): string => {
  if (!isoString) return '';
  const startTime = formatIsoTime(isoString);
  if (!durationMinutes || Number.isNaN(durationMinutes) || !startTime) {
    return startTime;
  }
  const [hoursStr, minutesStr] = startTime.split(':');
  const startMinutes = parseInt(hoursStr, 10) * 60 + parseInt(minutesStr, 10);
  const totalMinutes = startMinutes + durationMinutes;
  const minutesInDay = 24 * 60;
  const normalized = ((totalMinutes % minutesInDay) + minutesInDay) % minutesInDay;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const getMinutesLeftLikeSchedule = (
  order: Order | DriverOrder,
  departmentTimeZone?: number | null
): number | null => {
  if (!order.plannedDate) return null;
  const endTime = calculateEndTime(order.plannedDate, order.plannedDateDuration ?? undefined);
  if (!endTime) return null;
  const currentTime = getDepartmentCurrentTime(departmentTimeZone);
  const currentDate = getDepartmentCurrentDate(departmentTimeZone);
  const plannedDateOnly = order.plannedDate ? order.plannedDate.split('T')[0] : null;
  const diffMinutesValue = diffInMinutes(endTime, currentTime);
  const isPlannedDayInPast = Boolean(plannedDateOnly && plannedDateOnly < currentDate);
  return isPlannedDayInPast ? 0 : diffMinutesValue < 0 ? -diffMinutesValue : 0;
};

export const getTimeUntilDeadline = (
  deadline: dayjs.Dayjs,
  _departmentTimeZone?: number | null
): number => {
  const plannedEnd = dayjs.utc(deadline);
  const now = dayjs.utc();
  const diffMinutes = plannedEnd.diff(now, 'minutes');
  return Math.max(0, diffMinutes);
};

export const countExpressOrders = (
  orders: (Order | DriverOrder)[],
  _departmentTimeZone?: number | null
): number => {
  let count = 0;
  for (const order of orders) {
    if (!isExpressOrder(order) || isDelivered(order)) continue;
    const minutesLeft = getMinutesLeftLikeSchedule(order, _departmentTimeZone);
    if (minutesLeft == null || minutesLeft > 0) count += 1;
  }
  return count;
};

export const hasAnyExpressOrder = (orders: (Order | DriverOrder)[]): boolean => {
  return orders.some((o) => isExpressOrder(o));
};
export const getExpressOrderColor = (
  order: Order | DriverOrder,
  departmentTimeZone?: number | null
): string | null => {
  if (!isExpressOrder(order)) {
    return null;
  }

  if (isDelivered(order)) {
    return null;
  }

  const minutesLeft = getMinutesLeftLikeSchedule(order, departmentTimeZone);
  if (minutesLeft == null || minutesLeft <= 0) {
    return null;
  }
  if (minutesLeft < 15) {
    return '#8B0000';
  }
  if (minutesLeft < 30) {
    return '#FF0000';
  }
  return '#FFD700';
};

export const getExpressDriverColor = (
  orders: (Order | DriverOrder)[],
  departmentTimeZone?: number | null
): string | null => {
  const activeExpress: number[] = [];
  for (const order of orders) {
    if (!isExpressOrder(order) || isDelivered(order)) continue;
    const minutesLeft = getMinutesLeftLikeSchedule(order, departmentTimeZone);
    if (minutesLeft && minutesLeft > 0) activeExpress.push(minutesLeft);
  }

  if (activeExpress.length === 0) {
    return null;
  }

  const minTimeRemaining = Math.min(...activeExpress);

  if (minTimeRemaining < 15) {
    return '#8B0000';
  }
  if (minTimeRemaining < 30) {
    return '#FF0000';
  }
  return '#FFD700';
};

export const formatTimeRemaining = (minutes: number): string => {
  if (minutes <= 0) {
    return 'overdue';
  }

  if (minutes < 60) {
    return `${minutes}min left`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h left`;
  }

  return `${hours}h ${remainingMinutes}min left`;
};

export const getMostUrgentExpressTimeRemainingMinutes = (
  orders: (Order | DriverOrder)[],
  departmentTimeZone?: number | null
): number | null => {
  const expressOrders = orders.filter((o) => isExpressOrder(o) && !isDelivered(o));

  if (expressOrders.length === 0) {
    return null;
  }

  let minTimeRemaining = Infinity;

  for (const order of expressOrders) {
    const deadline = getExpressOrderDeadline(order, departmentTimeZone);
    if (deadline) {
      const timeRemaining = getTimeUntilDeadline(deadline, departmentTimeZone);
      minTimeRemaining = Math.min(minTimeRemaining, timeRemaining);
    }
  }

  return minTimeRemaining === Infinity ? null : minTimeRemaining;
};

export const getMostUrgentExpressTimeRemaining = (
  orders: (Order | DriverOrder)[],
  departmentTimeZone?: number | null
): string | null => {
  const timeRemainingMinutes = getMostUrgentExpressTimeRemainingMinutes(orders, departmentTimeZone);

  if (timeRemainingMinutes === null) {
    return null;
  }

  return formatTimeRemaining(Math.max(0, timeRemainingMinutes));
};

export const getDepartmentCurrentTime = (departmentTimeZone?: number | null): string => {
  return dayjs.utc().utcOffset(timezoneToMinutes(departmentTimeZone)).format('HH:mm');
};

export const getDepartmentCurrentDate = (departmentTimeZone?: number | null): string => {
  return dayjs.utc().utcOffset(timezoneToMinutes(departmentTimeZone)).format('YYYY-MM-DD');
};

export const diffInMinutes = (a: string, b: string): number => {
  const t1 = dayjs(a, 'HH:mm');
  const t2 = dayjs(b, 'HH:mm');
  return t2.diff(t1, 'minute');
};
